import { useState, useEffect, useCallback, useRef } from 'react';

import LocationObject from 'entities/locationObject/model';
import { renderAddress } from 'utils/renderAddress';

import {
  PresetAction,
  ChatMessage,
  ChoiceOption,
  MethodServiceType,
  ContextStore,
  ContextValue,
  ArrayContextValue,
  Preset,
} from '../types';

import { useImpactAiContext } from './ImpactAiContextProvider';
import { ImpactAiTracker } from './useImpactAiTracking';
import { resolvePayload } from './helpers/resolvePayload';

interface ActionProcessorOptions {
  messageDelay?: number;
  wordDelay?: number;
  methodService?: MethodServiceType;
  tracker?: ImpactAiTracker;
  allPresets?: Preset[];
}

interface ActionChange {
  type: 'insert';
  insertIndex: number;
  actions: PresetAction[];
}

interface HistoryEntry {
  actionIndex: number;
  actionType: string;
  choiceId?: string;
  messages: number; // Number of messages at this point
  actionChanges: ActionChange[]; // Track changes made to the action list
  contextSnapshot?: ContextStore; // Snapshot of context at this point for rollback
}

interface ActionProcessorResult {
  messages: ChatMessage[];
  currentAction: PresetAction | null;
  completedActions: number;
  isProcessingAction: boolean;
  wordDelay: number;
  canGoBack: boolean;
  handleActionComplete: () => void;
  handleUserInput: (inputKey: string, value: any) => void;
  handleLocationInput: (inputKey: string, value: LocationObject | string) => void;
  handleSubmitTemplate: (template: string) => void;
  handleChoiceSelect: (choiceKey: string, choice: ChoiceOption) => void;
  handleMultipleChoiceSubmit: (
    selectedChoices: Array<{ key: string; choice: ChoiceOption }>
  ) => void;
  handleGoBack: (specificIndex?: number) => void;
  clearScriptUIMessages: () => void;
}

const useImpactAIActionProcessor = (
  initialActions: PresetAction[],
  options: ActionProcessorOptions = {}
): ActionProcessorResult => {
  const {
    messageDelay = 1000,
    wordDelay = 50,
    methodService = async () => {},
    tracker,
    allPresets = [],
  } = options;

  const originalActionsRef = useRef<PresetAction[]>([...initialActions]);
  const actionChangesRef = useRef<ActionChange[]>([]);

  const contextApi = useImpactAiContext();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [actions, setActions] = useState<PresetAction[]>(initialActions);
  const [currentAction, setCurrentAction] = useState<PresetAction | null>(null);
  const [completedActions, setCompletedActions] = useState(0);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [actionHistory, setActionHistory] = useState<HistoryEntry[]>([]);

  const canGoBack = actionHistory.length > 0;

  const resolveSubPreset = useCallback(
    (subPresetAction: PresetAction): PresetAction[] => {
      if (subPresetAction.type !== 'subPreset') {
        return [subPresetAction];
      }

      // Use resolvePayload to resolve the presetId in case it contains context variables
      const resolvedPayload = resolvePayload(
        { presetId: subPresetAction.data.presetId },
        contextApi.getFullContext()
      );
      const presetId = resolvedPayload.presetId as string;

      if (!presetId) {
        return [];
      }

      const targetPreset = allPresets.find((preset) => preset.id === presetId);

      if (!targetPreset) {
        return [];
      }

      // Return the target preset's actions with source tracking
      const resolvedActions = targetPreset.actions.map((action) => ({
        ...action,
        sourcePresetId: targetPreset.id,
        sourcePresetTitle: targetPreset.title,
      }));

      return resolvedActions;
    },
    [allPresets, contextApi]
  );

  const insertActions = useCallback(
    (newActions: PresetAction[], index?: number) => {
      const insertIndex = typeof index === 'number' ? index : completedActions + 1;

      // Record this change for history
      actionChangesRef.current.push({
        type: 'insert',
        insertIndex,
        actions: [...newActions],
      });

      setActions((currentActions) => {
        const updatedActions = [
          ...currentActions.slice(0, insertIndex),
          ...newActions,
          ...currentActions.slice(insertIndex),
        ];

        return updatedActions;
      });
    },
    [completedActions]
  );

  const handleActionComplete = useCallback(() => {
    if (!currentAction) {
      return;
    }

    if (currentAction.type === 'message') {
      setMessages((prev) =>
        prev.map((msg, idx) =>
          idx === prev.length - 1
            ? { ...msg, isAnimating: false, isUser: currentAction.data.actor === 'user' }
            : msg
        )
      );
    }

    // Store a history entry for interactive actions
    if (
      (currentAction.type === 'choice' ||
        currentAction.type === 'multipleChoice' ||
        currentAction.type === 'templateInput' ||
        currentAction.type === 'locationInput' ||
        currentAction.type === 'call') &&
      !isProcessingAction
    ) {
      setActionHistory((prev) => [
        ...prev,
        {
          actionIndex: completedActions,
          actionType: currentAction.type,
          choiceId:
            currentAction.type === 'choice' || currentAction.type === 'multipleChoice'
              ? currentAction.data.choiceId
              : undefined,
          messages: messages.length,
          actionChanges: [...actionChangesRef.current],
          contextSnapshot: contextApi.getFullContext(),
        },
      ]);
    }

    setCompletedActions((prev) => {
      const newCompletedActions = prev + 1;
      return newCompletedActions;
    });
    setIsProcessingAction(false);
  }, [currentAction, completedActions, messages.length, isProcessingAction, contextApi]);

  const handleUserInput = useCallback(
    (inputKey: string, value: string) => {
      contextApi.setValue(inputKey, value);
    },
    [contextApi]
  );

  const handleLocationInput = useCallback(
    (inputKey: string, value: LocationObject | string) => {
      // LocationObject is a class but can be serialized as an object
      // Convert to plain object if it's a LocationObject instance
      const contextValue: ContextValue =
        typeof value === 'string' ? value : value.toJS ? value.toJS() : value;
      contextApi.setValue(inputKey, contextValue);

      setMessages((prev) => [
        ...prev,
        {
          text: typeof value === 'string' ? value : renderAddress(value),
          isUser: true,
          canGoBack: true,
          actionIndex: completedActions,
        },
      ]);

      handleActionComplete();
    },
    [completedActions, contextApi, handleActionComplete]
  );

  const handleChoiceSelect = useCallback(
    (_choiceKey: string, choice: ChoiceOption) => {
      if (!currentAction || currentAction.type !== 'choice') {
        return;
      }

      // Validate and set the choice value
      const { value } = choice;
      let contextValue: ContextValue;

      if (
        value === null ||
        value === undefined ||
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        (typeof value === 'object' && value !== null)
      ) {
        contextValue = value as ContextValue;
      } else {
        // If value is not a valid type, convert to string
        contextValue = String(value);
      }

      contextApi.setValue(currentAction.data.choiceId, contextValue);

      // Add a message representation of the users choice as a reminder and history anchor
      setMessages((prev) => [
        ...prev,
        {
          text: choice.text,
          isUser: true,
          canGoBack: true,
          actionIndex: completedActions,
        },
      ]);

      handleActionComplete();
    },
    [currentAction, handleActionComplete, completedActions, contextApi]
  );

  const handleMultipleChoiceSubmit = useCallback(
    (selectedChoices: Array<{ key: string; choice: ChoiceOption }>) => {
      if (!currentAction || currentAction.type !== 'multipleChoice') {
        return;
      }

      // Store the selected values as an array
      // Filter and validate that values are valid ContextValues
      const selectedValues: ContextValue[] = selectedChoices.map(({ choice }) => {
        const { value } = choice;
        // Validate that the value is a valid ContextValue
        if (
          value === null ||
          value === undefined ||
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean' ||
          (typeof value === 'object' && value !== null)
        ) {
          return value as ContextValue;
        }
        // If value is not a valid type, convert to string
        return String(value);
      });
      // ArrayContextValue type expects specific array structure
      const arrayValue: ArrayContextValue = selectedValues as ArrayContextValue;
      contextApi.setValue(currentAction.data.choiceId, arrayValue);

      // Create a summary message of the user's selections
      const selectionTexts = selectedChoices.map(({ choice }) => choice.text);
      const summaryText =
        selectionTexts.length > 1
          ? `Selected: ${selectionTexts.slice(0, -1).join(', ')}, and ${
              selectionTexts[selectionTexts.length - 1]
            }`
          : `Selected: ${selectionTexts[0]}`;

      setMessages((prev) => [
        ...prev,
        {
          text: summaryText,
          isUser: true,
          canGoBack: true,
          actionIndex: completedActions,
        },
      ]);

      handleActionComplete();
    },
    [currentAction, handleActionComplete, completedActions, contextApi]
  );

  const handleSubmitTemplate = useCallback(
    (template: string) => {
      const parsedTemplate = contextApi.parseTemplate(template);

      // Add a message representation of the users input as a reminder and history anchor
      setMessages((prev) => [
        ...prev,
        {
          text: parsedTemplate,
          isUser: true,
          canGoBack: true,
          actionIndex: completedActions,
        },
      ]);

      handleActionComplete();
    },
    [handleActionComplete, completedActions, contextApi]
  );

  const handleGoBack = useCallback(
    (specificIndex?: number) => {
      if (!canGoBack) {
        return;
      }

      let targetIndex = 0;
      let historyEntry: HistoryEntry | null = null;

      if (specificIndex !== undefined) {
        const entryIndex = actionHistory.findIndex((entry) => entry.actionIndex === specificIndex);
        if (entryIndex >= 0) {
          historyEntry = actionHistory[entryIndex];
          targetIndex = entryIndex;
        }
      } else {
        historyEntry = actionHistory[actionHistory.length - 1];
        targetIndex = actionHistory.length - 1;
      }

      if (!historyEntry) {
        return;
      }

      const entry = historyEntry;

      setActionHistory((prev) => prev.slice(0, targetIndex + 1));

      setMessages((prev) => prev.slice(0, entry.messages));

      setCompletedActions(entry.actionIndex);

      if (entry.contextSnapshot) {
        contextApi.resetContext();
        contextApi.mergeContext(entry.contextSnapshot);
      } else if (
        (entry.actionType === 'choice' || entry.actionType === 'multipleChoice') &&
        entry.choiceId
      ) {
        contextApi.deleteValue(entry.choiceId);
      }

      let restoredActions = [...originalActionsRef.current];

      if (entry.actionChanges.length > 0) {
        entry.actionChanges.forEach((change) => {
          if (change.type === 'insert') {
            restoredActions = [
              ...restoredActions.slice(0, change.insertIndex),
              ...change.actions,
              ...restoredActions.slice(change.insertIndex),
            ];
          }
        });
      }

      actionChangesRef.current = [...historyEntry.actionChanges];

      setActions(restoredActions);

      setIsProcessingAction(false);
    },
    [actionHistory, canGoBack, contextApi]
  );

  const clearScriptUIMessages = useCallback(() => {
    setMessages((prev) => prev.filter((msg) => !msg.isScriptUI));
  }, []);

  // Process the next action in the queue
  useEffect(() => {
    if (isProcessingAction || completedActions >= actions.length) {
      return;
    }

    const nextAction = actions[completedActions];

    // Handle subPreset actions dynamically
    if (nextAction.type === 'subPreset') {
      const resolvedActions = resolveSubPreset(nextAction);

      if (resolvedActions.length > 0) {
        // Replace the subPreset action with the resolved actions
        setActions((currentActions) => {
          const updatedActions = [
            ...currentActions.slice(0, completedActions),
            ...resolvedActions,
            ...currentActions.slice(completedActions + 1),
          ];

          return updatedActions;
        });

        // Record this change for history
        actionChangesRef.current.push({
          type: 'insert',
          insertIndex: completedActions,
          actions: resolvedActions,
        });
      } else {
        // If no actions resolved, skip this action
        setCompletedActions((prev) => prev + 1);
      }

      return;
    }

    setCurrentAction(nextAction);

    const timeoutId = setTimeout(() => {
      if (nextAction.type === 'message') {
        setIsProcessingAction(true);

        const parsedText = contextApi.parseTemplate(nextAction.data.text);

        setMessages((prev) => [
          ...prev,
          {
            text: parsedText,
            isUser: nextAction.data.actor === 'user',
            isAnimating: nextAction.data.actor === 'bot',
            showSpinner: !!nextAction.data.delay,
            spinnerTime: nextAction.data.delay || 0,
          },
        ]);

        if (nextAction.data.actor === 'user') {
          setTimeout(() => {
            handleActionComplete();
          }, 100);
        }
      } else if (nextAction.type === 'call') {
        setIsProcessingAction(true);

        let startMessageComplete = false;
        if (nextAction.data.ui?.startMessage) {
          setMessages((prev) => [
            ...prev,
            {
              text: nextAction.data.ui?.startMessage ?? '',
              isUser: false,
              isAnimating: true,
              isUIMessage: true,
              onUIComplete: () => {
                startMessageComplete = true;
              },
            },
          ]);
        } else {
          startMessageComplete = true;
        }

        const executeMethod = async () => {
          const startTime = Date.now();
          let success = false;
          let actionCompleted = false;

          // Create UI state callback
          const uiStateCallback = (
            phase: 'start' | 'processing' | 'complete' | 'error' | 'script_ui_needed',
            handleRetry?: () => void,
            scriptUIData?: any
          ) => {
            if (nextAction.data.ui) {
              if (phase === 'processing' && nextAction.data.ui.showSpinner) {
                const addSpinnerWhenReady = () => {
                  if (startMessageComplete) {
                    setMessages((prev) => {
                      const hasSpinner = prev.some((msg) => msg.showSpinner);
                      if (hasSpinner) {
                        return prev;
                      }
                      return [
                        ...prev,
                        {
                          text: '',
                          isUser: false,
                          showSpinner: true,
                          spinnerTime: 0,
                        },
                      ];
                    });
                  } else {
                    setTimeout(addSpinnerWhenReady, 50);
                  }
                };

                addSpinnerWhenReady();
              } else if (phase === 'error') {
                setMessages((prev) => prev.filter((msg) => !msg.showSpinner));
                setMessages((prev) => [
                  ...prev.filter((msg) => !msg.isError),
                  {
                    text: 'Oh no! Something went wrong.',
                    isUser: false,
                    isAnimating: false,
                    isUIMessage: true,
                    isError: true,
                    onRetry: () => {
                      setMessages((innerPrev) => {
                        const next = innerPrev.filter((msg) => !msg.isError);
                        const hasSpinner = next.some((msg) => msg.showSpinner);
                        if (hasSpinner) {
                          return next;
                        }
                        return [
                          ...next,
                          {
                            text: 'Got it! Trying again...',
                            isUser: false,
                            showSpinner: true,
                            spinnerTime: 0,
                          },
                        ];
                      });
                      handleRetry?.();
                    },
                  },
                ]);
                if (tracker) {
                  tracker.trackError(
                    'MethodExecutionError',
                    nextAction.data.method,
                    completedActions,
                    false
                  );
                }
              } else if (phase === 'complete') {
                if (nextAction.data.ui?.showSpinner) {
                  setMessages((prev) => prev.filter((msg) => !msg.showSpinner));
                }

                if (nextAction.data.ui?.endMessage) {
                  setMessages((prev) => {
                    const hasEndMessage = prev.some(
                      (msg) => msg.text === nextAction.data.ui?.endMessage && msg.isUIMessage
                    );
                    if (hasEndMessage) {
                      return prev;
                    }
                    return [
                      ...prev,
                      {
                        text: nextAction.data.ui?.endMessage ?? '',
                        isUser: false,
                        isAnimating: true,
                        isUIMessage: true,
                      },
                    ];
                  });
                }

                // Only complete the action if it hasn't been completed yet
                if (!actionCompleted) {
                  actionCompleted = true;
                  handleActionComplete();
                }
              }
            } else if (phase === 'script_ui_needed' && scriptUIData) {
              // Handle script UI requests
              if (scriptUIData.type === 'choice') {
                setMessages((prev) => [
                  ...prev.filter((msg) => !msg.showSpinner),
                  {
                    text: scriptUIData.question || 'Please make a choice:',
                    isUser: false,
                    isScriptUI: true,
                    scriptUIType: 'choice',
                    scriptQuestion: scriptUIData.question,
                    scriptChoices: scriptUIData.choices,
                    onScriptResponse: scriptUIData.onResponse,
                  },
                ]);
              } else if (scriptUIData.type === 'input') {
                setMessages((prev) => [
                  ...prev.filter((msg) => !msg.showSpinner),
                  {
                    text: scriptUIData.question || 'Please provide input:',
                    isUser: false,
                    isScriptUI: true,
                    scriptUIType: 'input',
                    scriptQuestion: scriptUIData.question,
                    scriptPlaceholder: scriptUIData.placeholder,
                    onScriptResponse: scriptUIData.onResponse,
                  },
                ]);
              } else if (scriptUIData.type === 'message') {
                setMessages((prev) => [
                  ...prev.filter((msg) => !msg.showSpinner),
                  {
                    text: scriptUIData.message || 'Script message',
                    isUser: false,
                    isScriptUI: true,
                    scriptUIType: 'message',
                    onScriptResponse: scriptUIData.onResponse,
                  },
                ]);
                // Auto-complete message type
                setTimeout(() => scriptUIData.onResponse(), 100);
              }
            }
          };

          try {
            const response = await methodService(
              nextAction.data.method,
              nextAction.data.payload,
              uiStateCallback,
              tracker
            );

            if (response) {
              if (response.actions && response.actions.length > 0) {
                insertActions(response.actions, response.insertAtIndex);
              }

              if (response.contextUpdates) {
                if (typeof response.contextUpdates === 'object') {
                  contextApi.mergeContext(response.contextUpdates);
                } else {
                  throw new Error('Invalid context updates');
                }
              }
            }

            // Only complete if no error and hasn't been completed yet via uiStateCallback
            if (!response?.error && !actionCompleted) {
              success = true;
              actionCompleted = true;
              handleActionComplete();
            } else if (!response?.error) {
              success = true;
            }
          } catch (error) {
            console.error('Error executing method:', error);
            success = false;

            if (nextAction.data.ui) {
              uiStateCallback('error');
            }
          } finally {
            if (tracker) {
              const duration = Date.now() - startTime;
              tracker.trackMethodExecute(
                nextAction.data.method,
                nextAction.data.payload,
                success,
                duration,
                completedActions
              );
            }
          }
        };

        executeMethod().catch((error) => {
          console.error('Error executing method:', error);
        });
      }
    }, 0);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeoutId);
  }, [
    actions,
    completedActions,
    handleActionComplete,
    isProcessingAction,
    messageDelay,
    methodService,
    insertActions,
    contextApi,
    tracker,
    resolveSubPreset,
  ]);

  return {
    messages,
    currentAction,
    completedActions,
    isProcessingAction,
    wordDelay,
    canGoBack,
    handleActionComplete,
    handleUserInput,
    handleSubmitTemplate,
    handleChoiceSelect,
    handleMultipleChoiceSubmit,
    handleLocationInput,
    handleGoBack,
    clearScriptUIMessages,
  };
};

export default useImpactAIActionProcessor;
